import React from "react";
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { ReportHeader } from "../components/charting/pdf/shared/ReportHeader";
import { ReportFooter } from "../components/charting/pdf/shared/ReportFooter";
import { ReportTable } from "../components/charting/pdf/shared/ReportTable";
import { reportStyles } from "../components/charting/pdf/shared/styles";
import { getPitchers } from "../components/charting/pdf/shared/utils";

const STRIKE_RESULTS = new Set(["swinging_strike", "called_strike", "foul"]);
const BALL_RESULTS = new Set(["ball"]);

const TERMINAL_RESULTS = new Set([
  "strikeout_swinging",
  "strikeout_looking",
  "walk",
  "hit_by_pitch",
  "in_play",
]);

const norm = (v) => (typeof v === "string" ? v.toLowerCase() : "");
const isStrike = (r) => STRIKE_RESULTS.has(norm(r));
const isBall = (r) => BALL_RESULTS.has(norm(r));
const isTerminal = (pitch) =>
  TERMINAL_RESULTS.has(norm(pitch.result)) || !!pitch?.hitDetails?.result;

const pitchTime = (p) => {
  const t = p?.time ? new Date(p.time).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

const keyForPitch = (p) => {
  const pitcher = p?.pitcher?.name || "";
  const batter = p?.batter?.name || "";
  const inn = p?.inning ?? "";
  const tb = p?.topBottom ?? "";
  return `${pitcher}_${batter}_${inn}_${tb}`;
};

const groupPitchesByAtBat = (pitches) => {
  const sorted = [...pitches].sort((a, b) => pitchTime(a) - pitchTime(b));

  const atBats = [];
  let cur = null;
  let balls = 0;
  let strikes = 0;
  let prevKey = null;

  for (const p of sorted) {
    const pitcher = p?.pitcher?.name;
    const batter = p?.batter?.name;
    if (!pitcher || !batter) continue;

    const k = keyForPitch(p);
    const startNew =
      !cur || k !== prevKey || isTerminal(cur.pitches[cur.pitches.length - 1]);

    if (startNew) {
      cur = { key: k, pitches: [] };
      atBats.push(cur);
      balls = 0;
      strikes = 0;
      prevKey = k;
    }

    const countBefore = `${balls}-${strikes}`;
    cur.pitches.push({ ...p, countBefore });

    const r = p?.result;
    if (isStrike(r) && !(norm(r) === "foul" && strikes === 2)) {
      strikes = Math.min(strikes + 1, 2);
    } else if (isBall(r)) {
      balls = Math.min(balls + 1, 3);
    }
  }

  return atBats;
};

const pct = (num, den) => (den > 0 ? ((num / den) * 100).toFixed(1) : "0.0");

const calculateCountMetrics = (pitches) => {
  const atBats = groupPitchesByAtBat(pitches);

  let fpsStrike = 0;
  let fpsTotal = 0;

  let oneOneWin = 0;
  let oneOneLoss = 0;
  let oneOneTotal = 0;

  let earlyStrike = 0;
  let earlyTotal = 0;

  let twoKPutaway = 0;
  let twoKTotal = 0;

  let ahead = 0;
  let behind = 0;
  let totalPitches = 0;

  // helper for “from count X, next pitch strike%”
  const fromCountStrike = {};
  const trackFromCounts = new Set(["2-0", "3-1"]);
  for (const c of trackFromCounts) fromCountStrike[c] = { strike: 0, total: 0 };

  for (const ab of atBats) {
    if (!ab.pitches.length) continue;

    const first = ab.pitches[0];
    fpsTotal++;
    if (isStrike(first.result)) fpsStrike++;

    for (let i = 0; i < ab.pitches.length; i++) {
      const p = ab.pitches[i];
      totalPitches++;

      const [b, s] = p.countBefore.split("-").map((x) => parseInt(x, 10));

      if (i < 2) {
        earlyTotal++;
        if (isStrike(p.result)) earlyStrike++;
      }

      if (b < s) ahead++;
      if (b > s) behind++;

      if (p.countBefore === "1-1") {
        oneOneTotal++;
        if (isStrike(p.result)) oneOneWin++;
        else if (isBall(p.result)) oneOneLoss++;
      }

      if (s === 2) {
        twoKTotal++;
        const r = norm(p.result);
        if (r === "strikeout_swinging" || r === "strikeout_looking") {
          twoKPutaway++;
        }
      }

      if (fromCountStrike[p.countBefore]) {
        fromCountStrike[p.countBefore].total++;
        if (isStrike(p.result)) fromCountStrike[p.countBefore].strike++;
      }
    }
  }

  return {
    fpsRate: pct(fpsStrike, fpsTotal),
    oneOneWinRate: pct(oneOneWin, oneOneTotal),
    oneOneLossRate: pct(oneOneLoss, oneOneTotal),
    earlyStrikeRate: pct(earlyStrike, earlyTotal),
    twoKPutawayRate: pct(twoKPutaway, twoKTotal),
    aheadRate: pct(ahead, totalPitches),
    behindRate: pct(behind, totalPitches),
    twoZeroStrikeRate: pct(fromCountStrike["2-0"]?.strike || 0, fromCountStrike["2-0"]?.total || 0),
    threeOneStrikeRate: pct(fromCountStrike["3-1"]?.strike || 0, fromCountStrike["3-1"]?.total || 0),
  };
};

export const CountManagementReport = ({ charts = [] }) => {
  const pitchers = getPitchers(charts);

  if (!charts.length || !pitchers.length) {
    return (
      <Document>
        <Page size="A4" style={reportStyles.page}>
          <Text>No data available to generate the report.</Text>
        </Page>
      </Document>
    );
  }

  const pitcherMetrics = pitchers.map((pitcherName) => {
    const pitcherPitches = charts.flatMap((chart) =>
      (chart.pitches || []).filter((p) => p?.pitcher?.name === pitcherName)
    );

    return {
      pitcherName,
      ...calculateCountMetrics(pitcherPitches),
    };
  });

  const headers = [
    "Pitcher",
    "FPS%",
    "1-1 Win% (to 1-2)",
    "1-1 Loss% (to 2-1)",
    "2-0 Strike%",
    "3-1 Strike%",
    "Early Strike% (1st 2)",
    "2K Putaway%",
    "Ahead%",
    "Behind%",
  ];

  const rows = pitcherMetrics.map((m) => ({
    cells: [
      { value: m.pitcherName },
      { value: `${m.fpsRate}%` },
      { value: `${m.oneOneWinRate}%` },
      { value: `${m.oneOneLossRate}%` },
      { value: `${m.twoZeroStrikeRate}%` },
      { value: `${m.threeOneStrikeRate}%` },
      { value: `${m.earlyStrikeRate}%` },
      { value: `${m.twoKPutawayRate}%` },
      { value: `${m.aheadRate}%` },
      { value: `${m.behindRate}%` },
    ],
  }));

  const allPitches = charts.flatMap((chart) => chart.pitches || []);
  const totalMetrics = calculateCountMetrics(allPitches);
  const totalRow = {
    cells: [
      { value: "TOTAL", bold: true },
      { value: `${totalMetrics.fpsRate}%`, bold: true },
      { value: `${totalMetrics.oneOneWinRate}%`, bold: true },
      { value: `${totalMetrics.oneOneLossRate}%`, bold: true },
      { value: `${totalMetrics.twoZeroStrikeRate}%`, bold: true },
      { value: `${totalMetrics.threeOneStrikeRate}%`, bold: true },
      { value: `${totalMetrics.earlyStrikeRate}%`, bold: true },
      { value: `${totalMetrics.twoKPutawayRate}%`, bold: true },
      { value: `${totalMetrics.aheadRate}%`, bold: true },
      { value: `${totalMetrics.behindRate}%`, bold: true },
    ],
    backgroundColor: "#E5E7EB",
  };

  const chunks = [];
  for (let i = 0; i < rows.length; i += 19) {
    const chunk = rows.slice(i, i + 19);
    chunk.push(totalRow);
    chunks.push(chunk);
  }

  return (
    <Document>
      {chunks.map((chunk, pageIndex) => (
        <Page
          key={pageIndex}
          size="A4"
          orientation="landscape"
          style={reportStyles.page}
        >
          <ReportHeader title="Count Management Report" />

          <ReportTable
            headers={headers}
            rows={chunk}
            cellStyles={{ header: Array(headers.length).fill({ fontSize: 9 }) }}
          />

          <View style={{ marginTop: 20, padding: 10, backgroundColor: "#F9FAFB" }}>
            <Text style={{ fontSize: 9, lineHeight: 1.5, color: "#374151" }}>
              <Text style={{ fontWeight: "bold" }}>FPS%:</Text> First Pitch Strike percentage - percentage of first pitches that are strikes.{"\n"}
              <Text style={{ fontWeight: "bold" }}>1-1 Win%:</Text> Percentage of 1-1 counts that result in 1-2 (pitcher advantage).{"\n"}
              <Text style={{ fontWeight: "bold" }}>1-1 Loss%:</Text> Percentage of 1-1 counts that result in 2-1 (batter advantage).{"\n"}
              <Text style={{ fontWeight: "bold" }}>2-0 Strike%:</Text> Strike percentage when behind 2-0 in the count.{"\n"}
              <Text style={{ fontWeight: "bold" }}>3-1 Strike%:</Text> Strike percentage when behind 3-1 in the count.{"\n"}
              <Text style={{ fontWeight: "bold" }}>Early Strike%:</Text> Strike percentage on first two pitches of the at-bat.{"\n"}
              <Text style={{ fontWeight: "bold" }}>2K Putaway%:</Text> Strikeout percentage when pitcher has two strikes.{"\n"}
              <Text style={{ fontWeight: "bold" }}>Ahead%:</Text> Percentage of pitches thrown while ahead in the count (more strikes than balls).{"\n"}
              <Text style={{ fontWeight: "bold" }}>Behind%:</Text> Percentage of pitches thrown while behind in the count (more balls than strikes).
            </Text>
          </View>

          <ReportFooter />
        </Page>
      ))}
    </Document>
  );
};
